import { ReactElement } from "react";

import "./MigrateDeprecationsPreview.scss";

/**
 * Dry-run preview payload returned by
 * `PATCH /<resource>/<id>/migrate_deprecations?dry_run=true`.
 * Script-based resources (flow/wrapper/scheduler/python_module) return
 * `has_syntax_error`; connectors do not. Both return the pre-computed unified
 * `diff` string, the residual `errors`, and the proposed-state `has_deprecation`.
 */
export interface MigrateDeprecationsPreviewData {
    diff?: string;
    errors?: string[];
    has_deprecation?: boolean | null;
    has_syntax_error?: boolean;
}

interface MigrateDeprecationsPreviewProps {
    name?: string;
    preview: MigrateDeprecationsPreviewData;
}

export type DiffLineKind = "add" | "del" | "hunk" | "meta" | "context";

export const classifyDiffLine = (line: string): DiffLineKind => {
    if (line.startsWith("+++") || line.startsWith("---")) {
        return "meta";
    }
    if (line.startsWith("@@")) {
        return "hunk";
    }
    if (line.startsWith("+")) {
        return "add";
    }
    if (line.startsWith("-")) {
        return "del";
    }
    return "context";
};

/**
 * Renders a side-effect-free preview of what "Migrate deprecations" would change
 * on a record: a proposed-state summary line, the unified diff (current vs
 * proposed) with per-line colouring, and any residual errors. Shown inside the
 * confirm dialog before the migration is actually applied.
 */
const MigrateDeprecationsPreview = ({
    name,
    preview,
}: MigrateDeprecationsPreviewProps): ReactElement => {
    const {
        diff = "",
        errors = [],
        has_deprecation: hasDeprecation,
        has_syntax_error: hasSyntaxError,
    } = preview;

    const diffLines =
        diff.length > 0 ? diff.replace(/\n$/, "").split("\n") : [];

    let summaryKind: "clear" | "partial" | "syntax-error" = "clear";
    let summaryText =
        "Applying this migration will clear all deprecations from this record.";
    if (hasSyntaxError) {
        summaryKind = "syntax-error";
        summaryText =
            "This record has a syntax error and cannot be fully migrated. Fix the syntax error, then try again.";
    } else if (hasDeprecation) {
        summaryKind = "partial";
        summaryText =
            "Some deprecations cannot be migrated automatically and will remain after applying.";
    }

    return (
        <div className="migrate-deprecations-preview">
            <p className="migrate-deprecations-preview__intro">
                This previews the changes that migrating deprecations on{" "}
                {name ? <b>{name}</b> : "this record"} would make. Nothing is
                applied until you confirm.
            </p>
            <div
                className={`migrate-deprecations-preview__summary migrate-deprecations-preview__summary--${summaryKind}`}
            >
                {summaryText}
            </div>
            {diffLines.length > 0 ? (
                <pre
                    className="migrate-deprecations-preview__diff"
                    aria-label="Proposed changes"
                >
                    {diffLines.map((line, index) => (
                        <span
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            className={`migrate-deprecations-preview__diff-line migrate-deprecations-preview__diff-line--${classifyDiffLine(
                                line,
                            )}`}
                        >
                            {line === "" ? " " : line}
                        </span>
                    ))}
                </pre>
            ) : (
                <div className="migrate-deprecations-preview__empty">
                    No textual changes are proposed.
                </div>
            )}
            {errors.length > 0 && (
                <div className="migrate-deprecations-preview__errors">
                    <div className="migrate-deprecations-preview__errors-title">
                        Some parts could not be migrated:
                    </div>
                    <ul className="migrate-deprecations-preview__errors-list">
                        {errors.map((error, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={index}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default MigrateDeprecationsPreview;
